import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import {
  auth, googleProvider, db, doc, getDoc, setDoc,
} from '../firebase/config';
import Logo from '../components/Logo';
import {
  Mail, Lock, User, ChevronLeft, Heart, Briefcase,
  Eye, EyeOff, Loader2, ArrowLeft, CheckCircle2,
  Globe, ChevronDown, Phone,
} from 'lucide-react';

// ============================================================
// THEME PER ROLE
// ============================================================
const ROLE_THEME = {
  client: {
    label: 'Client',
    icon: Heart,
    accent: '#EFA9B6',
    accentDark: '#E88B9C',
    accentSoft: '#FBE4E8',
    accentText: '#8B5A66',
    buttonBg: 'bg-[#EFA9B6] hover:bg-[#E88B9C]',
    activePill: 'bg-[#EFA9B6] text-white',
    ring: 'focus:ring-[#EFA9B6]/40',
    linkText: 'text-[#E88B9C]',
  },
  professional: {
    label: 'Professional',
    icon: Briefcase,
    accent: '#8B5FBF',
    accentDark: '#7A4EAD',
    accentSoft: '#E8DEF7',
    accentText: '#6B4A96',
    buttonBg: 'bg-[#8B5FBF] hover:bg-[#7A4EAD]',
    activePill: 'bg-[#8B5FBF] text-white',
    ring: 'focus:ring-[#8B5FBF]/40',
    linkText: 'text-[#8B5FBF]',
  },
};

// ============================================================
// COUNTRY CODES & PHONE LENGTHS
// ============================================================
const COUNTRY_CODES = [
  { code: '+27', country: 'ZA', flag: '🇿🇦', length: 9 },
  { code: '+44', country: 'GB', flag: '🇬🇧', length: 10 },
  { code: '+1', country: 'US', flag: '🇺🇸', length: 10 },
  { code: '+263', country: 'ZW', flag: '🇿🇼', length: 9 },
  { code: '+267', country: 'BW', flag: '🇧🇼', length: 8 },
  { code: '+264', country: 'NA', flag: '🇳🇦', length: 9 },
  { code: '+266', country: 'LS', flag: '🇱🇸', length: 8 },
  { code: '+268', country: 'SZ', flag: '🇸🇿', length: 8 },
  { code: '+258', country: 'MZ', flag: '🇲🇿', length: 9 },
  { code: '+61', country: 'AU', flag: '🇦🇺', length: 9 },
  { code: '+33', country: 'FR', flag: '🇫🇷', length: 9 },
  { code: '+49', country: 'DE', flag: '🇩🇪', length: 10 },
  { code: '+91', country: 'IN', flag: '🇮🇳', length: 10 },
];

// ============================================================
// SECURITY HELPERS
// ============================================================
const ALLOWED_DOMAINS = [
  'gmail.com', 'icloud.com', 'hotmail.com', 'zoho.com', 'maconnect.co.za',
];

const isValidEmailDomain = (email) => {
  if (!email || !email.includes('@')) return false;
  const lower = email.toLowerCase().trim();
  if (lower.includes('example')) return false;
  const domain = lower.split('@')[1];
  return ALLOWED_DOMAINS.includes(domain);
};

const validatePassword = (password) => {
  if (!password) return false;
  if (password.toLowerCase().includes('password')) return false;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  return hasUpper && hasLower && hasNumber && hasSpecial && password.length >= 8;
};

const validatePhone = (phone, countryCode) => {
  if (!phone || !phone.trim()) return false;
  const digits = phone.replace(/\D/g, '');
  const country = COUNTRY_CODES.find(c => c.code === countryCode);
  return digits.length === (country ? country.length : 9);
};

const checkBannedWords = (text) => {
  if (!text) return false;
  const lower = text.toLowerCase();
  return lower.includes('example') || lower.includes('password');
};

const getStrikes = (email) => {
  try {
    const strikes = JSON.parse(localStorage.getItem('skytone_strikes') || '{}');
    return strikes[email.toLowerCase()] || 0;
  } catch {
    return 0;
  }
};

const addStrike = (email) => {
  try {
    const strikes = JSON.parse(localStorage.getItem('skytone_strikes') || '{}');
    const key = email.toLowerCase();
    strikes[key] = (strikes[key] || 0) + 1;
    localStorage.setItem('skytone_strikes', JSON.stringify(strikes));
    return strikes[key];
  } catch {
    return 1;
  }
};

const clearStrikes = (email) => {
  try {
    const strikes = JSON.parse(localStorage.getItem('skytone_strikes') || '{}');
    delete strikes[email.toLowerCase()];
    localStorage.setItem('skytone_strikes', JSON.stringify(strikes));
  } catch {}
};

// ============================================================
// MAIN
// ============================================================
const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getInitialMode = () => {
    const params = new URLSearchParams(location.search);
    const queryMode = params.get('mode');
    if (queryMode === 'login') return 'login';
    if (queryMode === 'register' || queryMode === 'signup') return 'register';
    if (location.pathname.includes('login')) return 'login';
    if (location.pathname.includes('register') || location.pathname.includes('signup')) return 'register';
    return 'login';
  };

  const getInitialRole = () => {
    if (location.state?.role === 'professional' || location.state?.role === 'pro') {
      return 'professional';
    }
    if (location.state?.role === 'client') {
      return 'client';
    }

    const params = new URLSearchParams(location.search);
    const queryRole = params.get('role')?.toLowerCase();
    if (queryRole === 'professional' || queryRole === 'pro') return 'professional';
    if (queryRole === 'client') return 'client';

    if (location.pathname.toLowerCase().includes('pro')) return 'professional';

    return sessionStorage.getItem('skytone_auth_role') || 'client';
  };

  const [mode, setMode] = useState(getInitialMode);
  const [role, setRole] = useState(getInitialRole);
  const [prevMode, setPrevMode] = useState('login');
  const [resetSent, setResetSent] = useState(false);

  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef(null);

  useEffect(() => {
    const nextMode = getInitialMode();
    if (nextMode === 'login' || mode !== 'forgot') setMode(nextMode);

    const detectedRole = getInitialRole();
    setRole(detectedRole);
    sessionStorage.setItem('skytone_auth_role', detectedRole);
  }, [location.pathname, location.search, location.state]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const theme = ROLE_THEME[role] || ROLE_THEME.client;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  const [countryCode, setCountryCode] = useState('+27');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const switchMode = (next) => {
    setResetSent(false);
    setError('');
    if (next === 'forgot') {
      setPrevMode(mode === 'forgot' ? prevMode : mode);
      setMode('forgot');
      return;
    }
    setMode(next);
    sessionStorage.setItem('skytone_auth_role', role);
    navigate(`${next === 'login' ? '/login' : '/register'}?role=${role}`, {
      replace: true,
      state: { role },
    });
  };

  const backToLoginFromForgot = () => {
    setResetSent(false);
    setError('');
    setMode(prevMode === 'register' ? 'login' : prevMode || 'login');
    sessionStorage.setItem('skytone_auth_role', role);
    navigate(`/login?role=${role}`, { replace: true, state: { role } });
  };

  const switchRole = (nextRole) => {
    setRole(nextRole);
    sessionStorage.setItem('skytone_auth_role', nextRole);
    setError('');
    navigate(`${location.pathname}?role=${nextRole}`, {
      replace: true,
      state: { role: nextRole },
    });
  };

  // ============================================================
  // ROUTE AFTER AUTH — strictly requires a valid profile document
  // ============================================================
  const routeAfterAuth = async (uid, verifiedRole = role) => {
    const fetchDoc = async () => {
      try {
        return await getDoc(doc(db, 'users', uid), { source: 'server' });
      } catch {
        return await getDoc(doc(db, 'users', uid));
      }
    };

    let userDoc = await fetchDoc();

    // Longer retry for slow networks / Firestore propagation
    if (!userDoc.exists()) {
      await new Promise(r => setTimeout(r, 1000));
      userDoc = await fetchDoc();
    }

    // CRITICAL: If the doc still doesn't exist, DO NOT navigate to any dashboard.
    // Sending them anywhere would render an empty page with no profile data.
    if (!userDoc.exists()) {
      await auth.signOut();
      setError('Could not load your profile. Please try logging in again.');
      setLoading(false);
      return;
    }

    const userData = userDoc.data();
    const actualRole = userData.role || userData.userType || verifiedRole;
    const isPro = actualRole === 'professional';

    if (userData.onboardingComplete === true) {
      navigate(isPro ? '/professional-dashboard' : '/dashboard', { replace: true });
    } else {
      navigate(isPro ? '/onboarding-professional' : '/onboarding-client', { replace: true });
    }
  };

  // ============================================================
  // SUBMIT
  // ============================================================
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;

    setError('');

    const emailKey = formData.email.trim();
    const currentStrikes = getStrikes(emailKey);

    if (currentStrikes >= 2) {
      setError('⛔ Your account has been flagged and is blocked.');
      return;
    }
    if (checkBannedWords(emailKey) || checkBannedWords(formData.password)) {
      const newStrikes = addStrike(emailKey);
      if (newStrikes >= 2) setError('⛔ Your account has been flagged and is blocked.');
      else setError('⚠️ Your account has been flagged. 1 more attempt remaining.');
      return;
    }

    if (mode === 'forgot') {
      if (!formData.email.trim()) {
        setError('Enter your email.');
        return;
      }
      if (!isValidEmailDomain(emailKey)) {
        setError('Please use a valid email (Gmail, iCloud, Hotmail, Zoho, or maconnect.co.za).');
        return;
      }
      setLoading(true);
      try {
        await sendPasswordResetEmail(auth, formData.email.trim());
        setResetSent(true);
      } catch (err) {
        if (err.code === 'auth/user-not-found') setResetSent(true);
        else if (err.code === 'auth/too-many-requests') {
          setError('Too many attempts. Please wait and try again.');
        } else {
          setError('Could not send reset email.');
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!isValidEmailDomain(emailKey)) {
      setError('Please use a valid email (Gmail, iCloud, Hotmail, Zoho, or maconnect.co.za).');
      return;
    }

    if (mode === 'register') {
      if (!validatePassword(formData.password)) {
        setError(
          'Password must be 8+ chars with 1 uppercase, 1 lowercase, 1 number, 1 special character. Cannot contain "password".'
        );
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (!validatePhone(formData.phone, countryCode)) {
        const reqLength = COUNTRY_CODES.find(c => c.code === countryCode)?.length || 9;
        setError(`Phone number for ${countryCode} must be exactly ${reqLength} digits.`);
        return;
      }
      if (!formData.name.trim()) {
        setError(role === 'professional' ? 'Business name is required.' : 'Full name is required.');
        return;
      }
    }

    setLoading(true);

    try {
      if (mode === 'login') {
        const cred = await signInWithEmailAndPassword(auth, formData.email, formData.password);

        const userDoc = await getDoc(doc(db, 'users', cred.user.uid));
        const userData = userDoc.exists() ? userDoc.data() : null;

        // CRITICAL: Do not proceed if the profile document is missing.
        // Without a doc, we have no role and no data — the dashboard would be empty.
        if (!userData) {
          await auth.signOut();
          setError('Account profile not found. Please contact support or try signing up again.');
          setLoading(false);
          return;
        }

        const actualRole = userData.role || userData.userType;

        if (actualRole !== role) {
          await auth.signOut();
          setError(
            `This account is registered as a ${actualRole}. Please switch to the ${actualRole} tab.`
          );
          setLoading(false);
          return;
        }

        clearStrikes(emailKey);
        await routeAfterAuth(cred.user.uid, actualRole);
      } else {
        const cred = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        await updateProfile(cred.user, { displayName: formData.name });

        const isPro = role === 'professional';
        const fullPhone = `${countryCode}${formData.phone.trim()}`;

        const baseDoc = {
          name: formData.name,
          nameLower: formData.name.trim().toLowerCase(),
          email: formData.email,
          phone: fullPhone,
          role,
          userType: role,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          preferences: [],
          coordinates: null,
          onboardingComplete: false,
          ...(isPro
            ? {
                businessName: formData.name.trim(),
                businessNameLower: formData.name.trim().toLowerCase(),
              }
            : {}),
        };

        await setDoc(doc(db, 'users', cred.user.uid), baseDoc);

        try {
          await sendEmailVerification(cred.user);
        } catch (err) {
          console.warn('Verification email failed:', err?.message);
        }

        clearStrikes(emailKey);
        navigate(role === 'client' ? '/onboarding-client' : '/onboarding-professional', {
          replace: true,
        });
      }
    } catch (err) {
      let message = 'Something went wrong. Please try again.';
      switch (err.code) {
        case 'auth/email-already-in-use':
          message = 'Email already registered. Try logging in.';
          break;
        case 'auth/invalid-email':
          message = 'Invalid email address.';
          break;
        case 'auth/weak-password':
          message = 'Password is too weak.';
          break;
        case 'auth/user-not-found':
          message = 'No account found with this email.';
          break;
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          message = 'Incorrect email or password.';
          break;
        case 'auth/too-many-requests':
          message = 'Too many attempts. Try again later.';
          break;
        case 'permission-denied':
          message = 'Could not load your profile. Please try again in a moment.';
          break;
        default:
          message = err.message || message;
      }
      setError(message);
      setLoading(false);
    }
  };

  // ============================================================
  // GOOGLE
  // ============================================================
  const handleGoogle = async () => {
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const displayName = result.user.displayName || 'User';
      const isPro = role === 'professional';

      if (mode === 'register') {
        const existingDoc = await getDoc(doc(db, 'users', result.user.uid));
        if (existingDoc.exists()) {
          const existingData = existingDoc.data();
          const existingRole = existingData.role || existingData.userType || role;
          if (existingRole !== role) {
            await auth.signOut();
            setError(
              `This Google account is already registered as a ${existingRole}. Please switch to the ${existingRole} tab.`
            );
            setLoading(false);
            return;
          }
          await routeAfterAuth(result.user.uid, existingRole);
          return;
        }

        const baseDoc = {
          name: displayName,
          nameLower: displayName.trim().toLowerCase(),
          email: result.user.email,
          phone: result.user.phoneNumber || '',
          role,
          userType: role,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          preferences: [],
          coordinates: null,
          onboardingComplete: false,
          ...(isPro
            ? {
                businessName: displayName.trim(),
                businessNameLower: displayName.trim().toLowerCase(),
              }
            : {}),
        };

        await setDoc(doc(db, 'users', result.user.uid), baseDoc);
        navigate(role === 'client' ? '/onboarding-client' : '/onboarding-professional', {
          replace: true,
        });
      } else {
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        const userData = userDoc.exists() ? userDoc.data() : null;

        if (!userData) {
          await auth.signOut();
          setError('Account profile not found. Please sign up first or contact support.');
          setLoading(false);
          return;
        }

        const actualRole = userData.role || userData.userType;

        if (actualRole !== role) {
          await auth.signOut();
          setError(
            `This account is registered as a ${actualRole}. Please switch to the ${actualRole} tab.`
          );
          setLoading(false);
          return;
        }

        await routeAfterAuth(result.user.uid, actualRole);
      }
    } catch (err) {
      console.error('Google auth failed:', err);
      setError('Failed to continue with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <>
      <div
        className="min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1522383225653-ed111181a951?auto=format&fit=crop&w=1600&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#F8B6C1]/60 via-[#EFA9B6]/40 to-[#8B5FBF]/30"></div>

        <div className="relative w-full max-w-lg z-10">
          <div className="backdrop-blur-2xl bg-white/25 border border-white/40 rounded-[2rem] shadow-2xl overflow-hidden">

            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              >
                <ChevronLeft size={18} />
                <span className="text-xs font-bold uppercase tracking-wider">Back</span>
              </button>
              <div className="flex items-center text-white">
                <Logo size={90} className="text-white" />
              </div>
            </div>

            {mode !== 'forgot' && (
              <div className="px-6 pt-4 pb-5">
                <div className="flex gap-1.5 p-1 bg-white/20 rounded-full border border-white/30 backdrop-blur-md">
                  {['client', 'professional'].map((r) => {
                    const t = ROLE_THEME[r];
                    const Icon = t.icon;
                    const active = role === r;
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => switchRole(r)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full text-xs font-bold transition-all ${
                          active
                            ? `${t.activePill} shadow-md`
                            : 'text-white/70 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <Icon size={13} />
                        {t.label}
                      </button>
                    );
                  })}
                  <div className="w-px bg-white/30 my-1"></div>
                  <button
                    type="button"
                    onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                    className="flex-1 py-2.5 rounded-full text-xs font-bold text-white/70 hover:text-white hover:bg-white/10 transition-all"
                  >
                    {mode === 'login' ? 'Sign Up' : 'Log In'}
                  </button>
                </div>
              </div>
            )}

            <div className="px-6 pb-6">
              <div className="bg-white/95 rounded-3xl p-6 shadow-lg">

                {mode === 'forgot' && (
                  <>
                    <button
                      type="button"
                      onClick={backToLoginFromForgot}
                      className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500 hover:text-gray-800 transition-colors mb-4"
                    >
                      <ArrowLeft size={13} /> Back to log in
                    </button>

                    {!resetSent ? (
                      <>
                        <div className="mb-5">
                          <h1 className="font-serif text-2xl font-bold text-[#3B2229] mb-1">
                            Reset password
                          </h1>
                          <p className="text-xs text-gray-500">
                            Enter the email you signed up with and we'll send you a reset link.
                          </p>
                        </div>
                        {error && (
                          <div className="bg-red-50 text-red-600 text-xs p-3 rounded-xl mb-4 border border-red-100">
                            {error}
                          </div>
                        )}
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <Field
                            icon={<Mail size={16} />}
                            name="email"
                            type="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                          <button
                            type="submit"
                            disabled={loading}
                            className={`w-full ${theme.buttonBg} text-white font-bold py-3.5 rounded-full disabled:opacity-50 flex items-center justify-center gap-2 touch-manipulation`}
                          >
                            {loading ? (
                              <>
                                <Loader2 size={16} className="animate-spin" /> Sending link…
                              </>
                            ) : (
                              'Send reset link'
                            )}
                          </button>
                        </form>
                      </>
                    ) : (
                      <div className="text-center py-2">
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                          style={{ backgroundColor: theme.accentSoft }}
                        >
                          <CheckCircle2 size={28} style={{ color: theme.accentDark }} />
                        </div>
                        <h1 className="font-serif text-2xl font-bold text-[#3B2229] mb-1">
                          Check your inbox
                        </h1>
                        <p className="text-xs text-gray-500 mb-6 leading-relaxed">
                          We sent a reset link to{' '}
                          <span className="font-bold text-gray-700">{formData.email}</span>.
                        </p>
                        <div
                          className="text-[11px] rounded-xl p-3 mb-5"
                          style={{
                            backgroundColor: theme.accentSoft,
                            color: theme.accentText,
                          }}
                        >
                          Didn't get it? Check spam, or wait a minute and try again.
                        </div>
                        <button
                          type="button"
                          onClick={() => { setResetSent(false); setError(''); }}
                          className={`w-full ${theme.buttonBg} text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 touch-manipulation`}
                        >
                          Send again
                        </button>
                        <button
                          type="button"
                          onClick={backToLoginFromForgot}
                          className="mt-3 text-xs font-bold text-gray-500 hover:text-gray-800 touch-manipulation"
                        >
                          Back to log in
                        </button>
                      </div>
                    )}
                  </>
                )}

                {mode !== 'forgot' && (
                  <>
                    <div className="mb-5">
                      <h1 className="font-serif text-2xl font-bold text-[#3B2229] mb-1">
                        {mode === 'login'
                          ? 'Welcome back'
                          : `Join as ${theme.label.toLowerCase()}`}
                      </h1>
                      <p className="text-xs text-gray-500">
                        {mode === 'login'
                          ? `Sign in to your ${theme.label.toLowerCase()} account`
                          : role === 'client'
                          ? 'Start your beauty journey with Skytone'
                          : 'Grow your beauty business with Skytone'}
                      </p>
                    </div>

                    {error && (
                      <div className="bg-red-50 text-red-600 text-xs p-3 rounded-xl mb-4 border border-red-100">
                        {error}
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                      {mode === 'register' && (
                        <>
                          <Field
                            icon={<User size={16} />}
                            name="name"
                            type="text"
                            placeholder={role === 'professional' ? 'Business Name' : 'Full Name'}
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />

                          <div className="flex gap-2">
                            <div className="relative w-28 shrink-0" ref={countryDropdownRef}>
                              <button
                                type="button"
                                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                className="w-full flex items-center justify-between bg-transparent border-0 border-b border-[#E5C9D0] focus:border-[#EFA9B6] outline-none text-sm font-medium text-[#3B2229] py-3 pl-7 pr-2 transition-colors"
                              >
                                <span className="flex items-center gap-1.5">
                                  <Globe size={16} className="text-[#C9A5B0]" />
                                  {COUNTRY_CODES.find(c => c.code === countryCode)?.flag} {countryCode}
                                </span>
                                <ChevronDown size={14} className="text-[#C9A5B0]" />
                              </button>

                              {showCountryDropdown && (
                                <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-100 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                                  {COUNTRY_CODES.map((c) => (
                                    <button
                                      key={c.country}
                                      type="button"
                                      onClick={() => {
                                        setCountryCode(c.code);
                                        setShowCountryDropdown(false);
                                      }}
                                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#FCF8F9] flex items-center gap-2 ${
                                        countryCode === c.code ? 'bg-[#FFF5F6] font-bold' : ''
                                      }`}
                                    >
                                      <span>{c.flag}</span> {c.code}
                                      <span className="text-gray-400 text-xs ml-auto">
                                        {c.country}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="relative flex-1">
                              <input
                                name="phone"
                                type="tel"
                                placeholder="Phone (Required)"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full bg-transparent border-0 border-b border-[#E5C9D0] focus:border-[#EFA9B6] outline-none text-sm font-medium text-[#3B2229] placeholder:text-[#C9A5B0] pl-2 py-3"
                                required
                              />
                            </div>
                          </div>
                        </>
                      )}

                      <Field
                        icon={<Mail size={16} />}
                        name="email"
                        type="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />

                      <Field
                        icon={<Lock size={16} />}
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={
                          mode === 'register'
                            ? 'Password (8+ chars, 1 upper, 1 num, 1 special)'
                            : 'Password'
                        }
                        value={formData.password}
                        onChange={handleChange}
                        required
                        trailing={
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        }
                      />

                      {mode === 'register' && (
                        <Field
                          icon={<Lock size={16} />}
                          name="confirmPassword"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Confirm Password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          required
                        />
                      )}

                      {mode === 'login' && (
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => switchMode('forgot')}
                            className={`text-[11px] font-bold ${theme.linkText} hover:underline transition-colors`}
                          >
                            Forgot password?
                          </button>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={loading}
                        className={`w-full ${theme.buttonBg} text-white font-bold py-3.5 rounded-full transition-opacity active:opacity-90 shadow-md disabled:opacity-50 flex items-center justify-center gap-2 mt-2 touch-manipulation select-none`}
                      >
                        {loading ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            {mode === 'login' ? 'Signing in…' : 'Creating account…'}
                          </>
                        ) : mode === 'login' ? (
                          'Log In'
                        ) : (
                          `Create ${theme.label} Account`
                        )}
                      </button>
                    </form>

                    <div className="relative my-5">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-100"></div>
                      </div>
                      <div className="relative flex justify-center text-[10px]">
                        <span className="bg-white px-3 text-gray-400 font-bold uppercase tracking-wider">
                          or
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={handleGoogle}
                      disabled={loading}
                      type="button"
                      className="w-full border border-gray-200 text-gray-700 font-bold py-3 rounded-full hover:bg-gray-50 transition-colors touch-manipulation select-none flex items-center justify-center gap-2 text-sm"
                    >
                      <GoogleIcon />
                      Continue with Google
                    </button>

                    <p className="text-center text-xs text-gray-500 mt-5">
                      {mode === 'login'
                        ? "Don't have an account?"
                        : 'Already have an account?'}{' '}
                      <button
                        type="button"
                        onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                        className={`font-bold hover:underline ${theme.linkText} touch-manipulation`}
                      >
                        {mode === 'login' ? 'Sign Up' : 'Log In'}
                      </button>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Field = ({ icon, trailing, ...props }) => (
  <div className="relative">
    <div className="absolute left-0 top-1/2 -translate-y-1/2 text-[#C9A5B0]">{icon}</div>
    <input
      {...props}
      className="w-full bg-transparent border-0 border-b border-[#E5C9D0] focus:border-[#EFA9B6] outline-none text-sm font-medium text-[#3B2229] placeholder:text-[#C9A5B0] pl-7 pr-8 py-3 transition-colors"
    />
    {trailing && <div className="absolute right-0 top-1/2 -translate-y-1/2">{trailing}</div>}
  </div>
);

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
  </svg>
);

export default AuthPage;